/** "They have signed off for the day — still assign it?"
 *
 *  A question rather than a refusal: the Owner's rule is that whoever is assigning
 *  may go ahead once told. So the server answers `PERSON_DAY_CLOSED` with who has
 *  signed off and what they said, and the same request goes back with those ids in
 *  `acknowledge_closed`.
 *
 *  One component and one hook because three screens need it — the add drawer, the
 *  entry grid and the reassign form — and three copies of "resend with the ids"
 *  would drift the first time one of them was touched.
 *
 *  Their own remark is what makes this worth a dialog at all. "Ravi Deshmukh signed
 *  off at 16:10 — off to the registrar for the afternoon" is a decision somebody can
 *  make; "Ravi is unavailable" is a wall.
 */
import { useEffect, useRef, useState } from 'react'
import { ApiError, type SignedOff } from '@/api/client'
import { fmtStamp } from '@/components/ui'

/** Wraps a submit so a sign-off becomes a question instead of an error.
 *
 *  `send` takes the ids to acknowledge and does the request. On the first attempt it
 *  gets whatever it was already sending; if the server objects, the names are held
 *  and `confirm` replays the identical request with them accepted.
 */
export function useSignedOffGate(send: (acknowledge: string[]) => Promise<unknown>) {
  const [asking, setAsking] = useState<SignedOff[] | null>(null)

  async function attempt(acknowledge: string[] = []) {
    try {
      return await send(acknowledge)
    } catch (e) {
      if (e instanceof ApiError && e.isPersonDayClosed && e.signedOff.length) {
        setAsking(e.signedOff)
        // Swallowed deliberately: the caller's error banner must not fire, because
        // nothing has gone wrong — a question is outstanding.
        return undefined
      }
      throw e
    }
  }

  return {
    asking,
    attempt,
    cancel: () => setAsking(null),
    /** Resend with exactly the people who were shown. Never a blanket "yes": anybody
     *  who signs off between the warning and this click has not been seen by anybody,
     *  so the server warns about them afresh. */
    confirm: async () => {
      const ids = (asking ?? []).map((p) => p.user_id)
      setAsking(null)
      return attempt(ids)
    },
  }
}

export function SignedOffConfirm({ people, onCancel, onConfirm, busy }: {
  people: SignedOff[]
  onCancel: () => void
  onConfirm: () => void
  busy?: boolean
}) {
  const one = people.length === 1
  // A dialog has to take the keyboard, and this one never did: no autofocus, no
  // Escape, no containment. It appears at the top of the page on the way to filing a
  // list, so somebody who had just pressed the submit button was left with focus on a
  // button that no longer existed - Escape did nothing and the answer was several
  // Shift+Tabs away at the far end of the document.
  const yes = useRef<HTMLButtonElement>(null)
  const wrap = useRef<HTMLDivElement>(null)
  useEffect(() => { yes.current?.focus() }, [])

  return (
    <div ref={wrap}
         className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-label="Confirm assignment"
         onKeyDown={(e) => {
           if (e.key === 'Escape') { e.preventDefault(); onCancel(); return }
           if (e.key !== 'Tab') return
           // Contained: while a modal is up, Tab must not wander off behind it.
           const stops = [...(wrap.current?.querySelectorAll<HTMLElement>(
             'button:not([disabled])') ?? [])]
           if (stops.length < 2) return
           const first = stops[0]
           const last = stops[stops.length - 1]
           if (!e.shiftKey && document.activeElement === last) {
             e.preventDefault(); first.focus()
           } else if (e.shiftKey && document.activeElement === first) {
             e.preventDefault(); last.focus()
           }
         }}>
      <div className="card w-full max-w-md p-5">
        <b className="text-[14px]">
          {one ? `${people[0].name} has signed off for the day`
               : `${people.length} of these people have signed off for the day`}
        </b>
        <ul className="mt-3 space-y-2.5">
          {people.map((p) => (
            <li key={p.user_id} className="text-[12.8px]">
              <span className="font-medium">{p.name}</span>
              {p.closed_at && (
                <span className="mono text-xs text-ink-3"> · {fmtStamp(p.closed_at)}</span>
              )}
              {/* Their own words. Without this the dialog is a refusal with an
                  override button, which is a worse thing than either. */}
              {p.remarks && (
                <div className="text-[12.3px] text-ink-2 mt-0.5 bg-paper border border-rule
                                rounded px-2.5 py-1.5">{p.remarks}</div>
              )}
            </li>
          ))}
        </ul>
        <p className="text-[12.5px] text-ink-3 mt-3">
          Nothing new goes on {one ? 'their' : 'their'} list for that day unless you
          say so. {one ? 'They' : 'They'} can reopen the day themselves.
        </p>
        <div className="flex gap-2 mt-4">
          <button ref={yes} className="btn btn-good flex-1 justify-center"
                  disabled={busy} onClick={onConfirm}>
            {busy ? 'Filing…' : 'Assign anyway'}
          </button>
          <button className="btn flex-1 justify-center" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
